import React, { useMemo } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { AgGrid, AgGridReact, Flex, Grid } from "@jsq/ds/core";
import { ComplianceCaseStatus, ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { useGetProgramsQuery } from "~/graphql/supergraphHooks";
import { CaseStatusRenderer } from "~/pages/Compliance/ComplianceTable/CaseStatusRenderer";
import { CaseTypeRenderer } from "~/pages/Compliance/ComplianceTable/CaseTypeRenderer";
import { isEntityClientOnboardingCaseType } from "~/pages/Compliance/ComplianceTable/CaseTypeRenderer/utils";
import { ResolutionRenderer } from "~/pages/Compliance/ComplianceTable/ResolutionRenderer";
import { statusValueFormatter } from "~/pages/Compliance/ComplianceTable/utils";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { ProposedCasesGridRowItem } from "../../types";
import { ProposedCasesGraphqlDataSource } from "./proposedCasesGraphqlDataSource";
import { proposedCasesGrid } from "./styles_vanilla.css";

const PAGE_SIZE = 100;

interface ProposedCasesGridProps {
  onRowSelected: (caseId: number | null) => void;
  arenaId?: number;
  entityName?: string;
  caseType?: string;
  clientObjectId?: string;
  positionId?: string;
}

const ProposedCasesGrid: React.FC<ProposedCasesGridProps> = ({
  onRowSelected,
  arenaId,
  entityName,
  caseType,
  clientObjectId,
  positionId,
}) => {
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const closedCasesOnly = ioReusableProfilesEnabled && entityName != null;
  const dataSource = useMemo(
    () =>
      new ProposedCasesGraphqlDataSource({
        arenaId,
        entityName,
        caseType,
        clientObjectId,
        closedCasesOnly,
        positionId,
      }),
    [arenaId, caseType, clientObjectId, closedCasesOnly, entityName, positionId],
  );

  const programsResult = useGetProgramsQuery();
  const programTypeToName = useMemo(
    () =>
      Object.fromEntries(
        (programsResult.data?.programsAssignableToEntities ?? []).map((p) => [
          p.programType,
          p.name,
        ]),
      ),
    [programsResult.data],
  );
  const programTypes = useMemo(
    () => programsResult.data?.programsAssignableToEntities?.map((p) => p.programType),
    [programsResult.data],
  );

  const gridOptions: AgGridReact.AgGridReactProps<ProposedCasesGridRowItem>["gridOptions"] =
    useMemo(
      () => ({
        rowModelType: "serverSide",
        serverSideDatasource: dataSource,
        autoSizeStrategy: { type: "fitGridWidth" },
        rowSelection: "single",
        domLayout: "autoHeight",
        getRowId: (params) => String(params.data.id),
      }),
      [dataSource],
    );

  // When the picker is restricted to closed cases, a user-applied status filter could
  // contradict or override that constraint.
  const statusFilterColDef: Pick<AgGrid.ColDef, "filter" | "filterParams"> = closedCasesOnly
    ? { filter: false }
    : {
        filter: "agSetColumnFilter",
        filterParams: {
          values: Object.values(ComplianceCaseStatus),
          valueFormatter: statusValueFormatter,
        },
      };

  const colDefs: AgGrid.ColDef[] = [
    {
      field: "caseName",
      headerName: "Case Name",
      checkboxSelection: true,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
      },
    },
    {
      colId: "programTypes",
      field: "program",
      headerName: "Program",
      valueFormatter: (params: AgGrid.ValueFormatterParams<ProposedCasesGridRowItem>) => {
        return params.data?.program ?? "--";
      },
      filter: "agSetColumnFilter",
      filterParams: {
        // Known gap: if the filter is opened before useGetProgramsQuery resolves, values stay empty until refreshed.
        // This is aligning with the compliance case table filter behavior.
        values: programTypes,
        valueFormatter: (params: { value: string }) =>
          programTypeToName[params.value] ?? params.value,
      },
    },
    {
      field: "caseType",
      headerName: "Case Type",
      cellRenderer: (params: ICellRendererParams<ProposedCasesGridRowItem>) => (
        <CaseTypeRenderer params={params} />
      ),
      sortable: false,
      filter: false,
    },
    { field: "creationDate", headerName: "Creation Date" },
    {
      field: "status",
      headerName: "Status",
      cellRenderer: (params: ICellRendererParams<ProposedCasesGridRowItem>) => (
        <CaseStatusRenderer params={params} />
      ),
      ...statusFilterColDef,
    },
    {
      field: "resolution",
      headerName: "Resolution",
      cellRenderer: (params: ICellRendererParams<ProposedCasesGridRowItem>) => (
        <ResolutionRenderer
          resolution={params.data?.resolution ?? ""}
          status={params.data?.status}
          internal={
            !!caseType && isEntityClientOnboardingCaseType(caseType as ComplianceCaseTypeEnum)
          }
        />
      ),
      sortable: false,
    },
    {
      field: "assignee",
      headerName: "Assignee",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
      },
    },
  ];

  const handleRowSelected = (event: AgGrid.SelectionChangedEvent<ProposedCasesGridRowItem>) => {
    const selectedNodes = event.api.getSelectedNodes();
    onRowSelected(selectedNodes.length > 0 ? (selectedNodes[0]?.data?.id ?? null) : null);
  };

  return (
    <Flex className={proposedCasesGrid}>
      <Grid<ProposedCasesGridRowItem>
        gridOptions={gridOptions}
        columnDefs={colDefs}
        pagination
        paginationPageSize={PAGE_SIZE}
        paginationPageSizeSelector={false}
        cacheBlockSize={PAGE_SIZE}
        domLayout="autoHeight"
        onSelectionChanged={handleRowSelected}
      />
    </Flex>
  );
};

export default ProposedCasesGrid;
